import React, { useState, useEffect } from "react";
import { Tabs, Button, Input, Checkbox, DatePicker } from "antd";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import { pluck } from "ramda";

// == import i18n
import { useTranslation } from "react-i18next";

// == Import component
import ModalPrint from "components/ModalPrint/ModalPrint";
import Loader from "../SearchBar/Loader/Loader";

// == Import SCSS
import "./visitPlannedPage.less";

// == Import icons
import {
  MdPrint,
  MdCheckBoxOutlineBlank,
  MdCheckBox,
  MdChevronLeft,
  MdChevronRight,
  MdFileDownload,
} from "react-icons/md";
import { WiSunrise, WiSunset, WiDaySunny } from "react-icons/wi";
import { downloadStatus, updateVisitsFromDay } from "services/features/VisitsSlice";
import { visitorMatchSearch } from "helpers";

const VisitPlannedPage = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const { TabPane } = Tabs;
  const { Search } = Input;
  const CheckboxGroup = Checkbox.Group;

  const { currentVisits, isFetching, disablePrinter, useKeyCard } = useSelector(
    state => state.visits,
  );

  const defaultCheckedList = [];

  const [checkedList, setCheckedList] = useState(defaultCheckedList);
  const [indeterminate, setIndeterminate] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [searchVisitors, setSearch] = useState("");
  const [selectedTabs, setSelectedTabs] = useState("morning");
  const [isPrintVisible, setPrintIsVisible] = useState(false);
  const [date, setDate] = useState(moment());
  const [day, setDay] = useState(0);

  // array of visitors with associated visit
  const [userVisits, setUserVisits] = useState([]);
  const [selectedVisitors, setSelectedVisitors] = useState([]);

  useEffect(() => {
    dispatch(updateVisitsFromDay(date));
    // eslint-disable-next-line
  }, [date]);

  useEffect(() => {
    if (!isPrintVisible) dispatch(updateVisitsFromDay(date));
    // eslint-disable-next-line
  }, [isPrintVisible]);

  useEffect(() => {
    let newList = [];
    for (let visit of currentVisits) {
      newList = newList.concat(
        visit.visitors
          .map(visitor => ({
            ...visitor,
            visitId: visit.id,
            resident: visit.resident,
            startDate: visit.startDate,
            endDate: visit.endDate,
          }))
          .sort((v1, v2) => (v1.startDate < v2.startDate ? -1 : 1)),
      );
    }
    const midDay = date.tz("Europe/Paris").set("hour", 12).set("minute", 0).set("second", 1);

    if (selectedTabs === "morning") {
      newList = newList.filter(
        v =>
          moment(v.startDate).isBefore(midDay) &&
          moment(v.startDate).isAfter(moment().startOf("day")),
      );
    }
    if (selectedTabs === "afternoon") {
      newList = newList.filter(
        v =>
          moment(v.startDate).isSameOrAfter(midDay) &&
          moment(v.startDate).isAfter(moment().startOf("day")),
      );
    }
    if (searchVisitors.length > 0)
      newList = newList.filter(visitor => visitorMatchSearch(visitor, searchVisitors));

    setUserVisits(newList);
  }, [currentVisits, date, searchVisitors, selectedTabs]);

  useEffect(() => {
    if (moment().format("HH:mm:ss") > moment("12:00:01", "HH:mm:ss").format("HH:mm:ss")) {
      setSelectedTabs("afternoon");
    }
  }, []);

  const plainOptions = userVisits.map(visitor => {
    const start = moment(visitor.startDate);
    const todayStart = moment().startOf("day");
    const startHour = start.isBefore(todayStart)
      ? t("Since") + " " + start.format("DD MMMM")
      : start.format("HH:mm");

    return {
      label: (
        <>
          <p className="nameVisitor">
            {visitor.firstname} {visitor.lastname}
          </p>
          <p className="nameResident">
            Résident : {visitor.resident.firstname} {visitor.resident.lastname}
          </p>
          <p className="dateVisitor">{startHour}</p>
          <p className={"statusVisitor--" + (visitor.status || "expected")}>
            {t(visitor.status) || t("expected")}
          </p>
        </>
      ),
      value: `${visitor.visitId}-${visitor.id}`,
    };
  });

  useEffect(() => {
    const selectedVisitors = checkedList.map(id =>
      userVisits.find(v => `${v.visitId}-${v.id}` === id),
    );

    setSelectedVisitors(selectedVisitors);
    // eslint-disable-next-line
  }, [checkedList]);

  const onChange = (list, test) => {
    setCheckedList(list);
    setIndeterminate(!!list.length && list.length < plainOptions.length);
    setCheckAll(list.length === plainOptions.length);
  };

  const onChangeDate = date => {
    setDate(moment(date));
    const diff = moment(date).diff(moment(), "day");

    setDay(diff + 1);
  };

  const addDay = () => {
    setDate(moment(date).add(1, "day"));
    setDay(day + 1);
  };

  const subDay = () => {
    setDate(moment(date).subtract(1, "day"));

    setDay(day - 1);
  };

  const onCheckAllChange = e => {
    setCheckedList(e.target.checked ? pluck("value", plainOptions) : []);
    setIndeterminate(false);
    setCheckAll(e.target.checked);
  };

  const handleSearch = evt => {
    setSearch(evt.target.value);
    setCheckedList(defaultCheckedList);
    setCheckAll(false);
  };

  const showModal = () => {
    setPrintIsVisible(true);
  };

  return (
    <div className="visitPlanned" style={{ height: "100%", paddingTop: "130px" }}>
      {isFetching && <Loader />}
      <ModalPrint
        isVisible={isPrintVisible}
        setVisible={setPrintIsVisible}
        visitorCheck={selectedVisitors}
        setVisitorCheck={setCheckedList}
        setSearch={setSearch}
        setCheckAll={setCheckAll}
        useKeyCard={useKeyCard}
        limitToRegister={true}
      />
      <p className="visitPlanned__title">
        {t("VisitorsDeclared")} {date.format("dddd DD MMMM")}
      </p>
      <section
        className="visitPlanned__section"
        style={{ marginTop: "60px", position: "relative" }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "30px" }}>
          <Button className="visitPlanned__btn" onClick={subDay}>
            <MdChevronLeft />
          </Button>

          <div className="visitPlanned__form">
            <p style={{ margin: "0 10px 0 0" }}>Selectionnez un jour</p>
            <DatePicker
              format="DD-MM-yyyy"
              value={date}
              defaultValue={date}
              onChange={onChangeDate}
              size="large"
              allowClear={false}
            />
          </div>

          <Button className="visitPlanned__btn" onClick={addDay}>
            <MdChevronRight />
          </Button>
        </div>

        <Tabs
          activeKey={selectedTabs}
          onChange={val => {
            setSelectedTabs(val);
            setCheckedList(defaultCheckedList);
            setCheckAll(false);
          }}
        >
          <TabPane
            tab={
              <span>
                <WiSunrise />
                {t("Morning")}
              </span>
            }
            key="morning"
          ></TabPane>

          <TabPane
            tab={
              <span>
                <WiSunset />
                {t("Afternoon")}
              </span>
            }
            key="afternoon"
          ></TabPane>
          <TabPane
            tab={
              <span>
                <WiDaySunny />
                {t("Day")}
              </span>
            }
            key="day"
          ></TabPane>
        </Tabs>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "50px" }}>
          {selectedVisitors.length === 0 || disablePrinter ? null : (
            <Button
              style={{ marginRight: "10px", marginLeft: "20px" }}
              type="primary"
              size="large"
              onClick={() => {
                showModal();
                // Because on mobile, Ant-d has a problem with onscreen keyboard and webview which puh the screen when the keyboard is deployed
                window.scrollTo(0, 0);
              }}
              icon={<MdPrint style={{ verticalAlign: "-2px", marginRight: "8px" }} />}
            >
              {selectedVisitors.length === 1 ? t("PrintBadge") : t("PrintBadges")}
            </Button>
          )}
          <Button
            style={{ marginRight: "10px", marginLeft: "20px" }}
            type="primary"
            size="large"
            target="_blank"
            onClick={() => dispatch(downloadStatus())}
            icon={<MdFileDownload style={{ verticalAlign: "-2px", marginRight: "8px" }} />}
          >
            {t("ExportList")}
          </Button>
          <Search
            style={{ width: "320px", marginRight: "20px" }}
            placeholder="Rechercher un visiteur"
            onChange={handleSearch}
            value={searchVisitors}
            enterButton
            onClick={() => {
              setSelectedTabs("day");
            }}
          />
        </div>
        <section style={{ margin: "40px 20px 0 20px", position: "relative" }}>
          {plainOptions.length === 0 ? (
            <>
              <p className="visitPlanned__noVisits">{t("NoVisit")}</p>
            </>
          ) : checkedList.length === plainOptions.length ? (
            <Checkbox
              style={{ position: "absolute", top: "0", left: "0", display: "grid" }}
              className="checkbox__all--check"
              indeterminate={indeterminate}
              onChange={onCheckAllChange}
              checked={checkAll}
            >
              <MdCheckBox style={{ verticalAlign: "-2px", marginRight: "8px" }} />
              {t("SelectAll")}
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: "normal",
                  textTransform: "initial",
                  textAlign: "center",
                  margin: "0",
                }}
              >
                {selectedVisitors.length} {selectedVisitors.length === 1 ? "personne" : "personnes"}
              </p>
            </Checkbox>
          ) : (
            <Checkbox
              style={{ position: "absolute", top: "0", left: "0", display: "grid" }}
              className="checkbox__all"
              indeterminate={indeterminate}
              onChange={onCheckAllChange}
              checked={checkAll}
            >
              <MdCheckBoxOutlineBlank />
              {t("SelectAll")}
            </Checkbox>
          )}

          <CheckboxGroup options={plainOptions} value={checkedList} onChange={onChange} />
        </section>
      </section>
    </div>
  );
};

export default VisitPlannedPage;
